// TechnologyPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const TechnologyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const platformStats = [
        { number: '88%', label: 'of marketers say technology is critical to achieving their marketing objectives' },
        { number: '3.5x', label: 'higher ROI with integrated marketing technology stacks' },
        { number: '70%', label: 'of businesses plan to increase martech investment in the coming year' },
        { number: '40%', label: 'efficiency gain through automated marketing workflows' }
    ];

    const whyPlatformsMatter = [
        '📈 Better data accuracy and insights',
        '🤖 Smarter automation and optimisation',
        '🎯 More precise targeting and delivery',
        '📊 Improved measurement and reporting',
        '🚀 Stronger overall performance'
    ];

    const platformCategories = [
        {
            icon: 'fas fa-chart-line',
            title: 'Analytics & Data Platforms',
            desc: 'We use leading analytics and data management tools to ensure your insights are accurate, real-time and actionable.',
            features: [
                'Google Analytics 4 (GA4) — Industry-standard analytics platform for measuring website and app engagement, user behaviour, conversions and growth opportunities',
                'Data Warehousing & BI Tools — Centralise, visualise and analyse performance across channels',
                'Tag Management Platforms — Manage tracking scripts, pixels and events without constant developer involvement'
            ]
        },
        {
            icon: 'fas fa-search',
            title: 'SEO & Content Platforms',
            desc: 'Optimize performance and visibility with platforms that help you research, create and measure impactful SEO and content strategies.',
            features: [
                'Keyword & Rank Tracking Tools — Track keyword movement across regions and search engines',
                'Content Intelligence Tools — Identify content gaps, topic opportunities and user intent signals'
            ]
        },
        {
            icon: 'fas fa-ad',
            title: 'Paid Media & Advertising Platforms',
            desc: 'Our PPC, programmatic and paid social strategies are powered by platforms trusted across the industry.',
            features: [
                'Google Ads — Performance search and shopping campaigns',
                'Microsoft Advertising — Expand reach beyond Google with lower CPCs',
                'Paid Social Platforms — Meta Ads, TikTok Ads, LinkedIn Ads, Pinterest & Snapchat',
                'Programmatic Platforms — Smart, automated media buying with real-time bidding'
            ]
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Conversion & Optimisation Platforms',
            desc: 'To increase conversions and improve user experience, we work with tools that help us test, measure and refine.',
            features: [
                'A/B and Multivariate Testing Tools — Run experiments to learn what resonates best',
                'Heatmap & Behavioural Tools — Visualise user interactions to reduce friction'
            ]
        },
        {
            icon: 'fas fa-users',
            title: 'CRM, Attribution & Growth Tools',
            desc: 'Connect and activate your data with systems that help you convert insights into revenue.',
            features: [
                'CRM Integrations — Seamless lead tracking and pipeline visibility',
                'Attribution & Performance Platforms — Measure true value of every touchpoint'
            ]
        }
    ];

    const benefits = [
        ['Better data accuracy and insights', 'Real-time, actionable analytics that inform confident decision-making'],
        ['Smarter automation and optimisation', 'Reduced manual effort with intelligent, self-optimising systems'],
        ['More precise targeting and delivery', 'Reach the right audiences with sophisticated platform capabilities'],
        ['Improved measurement and reporting', 'Complete visibility into marketing performance and true ROI'],
        ['Stronger overall performance', 'Integrated technology stacks that drive measurable business outcomes']
    ];

    const targetBusinesses = [
        'Organisations seeking to build or optimise their marketing technology stack for improved efficiency and performance',
        'Businesses needing integrated platform solutions that connect data, analytics, and execution across multiple channels',
        'Companies requiring sophisticated analytics, attribution, and measurement capabilities to optimise marketing ROI',
        'Enterprises looking to leverage advanced advertising platforms for precise targeting and campaign optimisation',
        'Growth-focused businesses wanting to implement scalable technology infrastructure that supports expansion'
    ];

    const platformQuestions = [
        'What marketing technology platforms best support your specific business objectives and growth goals?',
        'How can integrated platform solutions improve your marketing efficiency and campaign performance?',
        'What analytics and measurement tools provide the most valuable insights for your decision-making?',
        'How do you ensure your technology stack remains adaptable to evolving market conditions?',
        'What platform configurations and integrations deliver the greatest operational and strategic value?'
    ];

    const platformTechniques = [
        'Strategic Platform Selection & Technology Architecture',
        'Cross-Platform Data Integration & Connectivity',
        'Analytics Implementation & Performance Measurement',
        'Advertising Platform Optimisation & Management',
        'Conversion Technology Configuration & Testing',
        'Martech Stack Integration & Workflow Automation'
    ];

    const martechChecklist = [
        'Tools are properly configured for accurate data',
        'Integrations work across platforms and teams',
        'Insights are surfaced clearly for decision makers',
        'Performance is maximised across campaigns'
    ];

    const partnerBenefits = [
        'Select tools that align with your business goals',
        'Connect platforms into a unified growth stack',
        'Configure systems for accuracy and scalability',
        'Train teams on best practices for long-term success'
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Technology</span>
                    </h1>
                    <p>Platforms We Work With — Powerful Tools for Smarter Digital Growth</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    At Reachtix, success in digital marketing starts with the right technology stack. We partner with top-tier platforms and tools that empower your campaigns, streamline workflows, and deliver measurable results — from analytics and optimisation to media buying and customer engagement.
                                </p>
                                <p className="sv-description">
                                    Rather than using generic solutions, we hand-select platforms that fit your goals, audience, and growth strategy. Whether you're scaling paid media, refining SEO, or tracking performance, our platform expertise gives you a competitive edge.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Explore Our Platforms
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        View All Platforms
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Platform Performance Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {platformStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* Why Platforms Matter Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Platforms Matter</h2>
                                <p className="sv-section-description">
                                    Choosing the right platforms impacts everything:
                                </p>
                                <div className="sv-questions-grid">
                                    {whyPlatformsMatter.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <span className="sv-question-icon">{item.substring(0, 2)}</span>
                                            {item.substring(3)}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-3">
                                    With the right set of tools in place, your digital ecosystem works smarter — not harder. Platforms are the foundation that enables strategy to turn into impactful action.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* What Are Platform Services Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Deliver</h2>
                                <p className="sv-section-description">
                                    We hand-select platforms that fit your goals, audience, and growth strategy. Our platform expertise gives you a competitive edge in today's complex digital landscape.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Platform Questions We Address:</h4>
                                    {platformQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Advanced Platform Techniques</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated platform methodologies and technical implementation strategies to ensure marketing technology delivers maximum value, operational efficiency, and strategic advantage.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Core Platform Methodologies:</h4>
                                    {platformTechniques.map((technique, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-cogs"></i>
                                            {technique}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Platform Categories Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Platform Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We partner with top-tier platforms and tools that empower your campaigns, streamline workflows, and deliver measurable results — from analytics and optimisation to media buying and customer engagement.
                                </p>

                                <div className="sv-approaches-grid">
                                    {platformCategories.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                                <ul className="sv-feature-list">
                                                    {item.features.map((feature, idx) => (
                                                        <li key={idx}>
                                                            <i className="fas fa-check-circle"></i>
                                                            {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Martech That Works With Your Strategy Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Martech That Works With Your Strategy</h2>
                                <p className="sv-section-description">
                                    Every business has unique needs — so we customise platform selections to match your growth goals, audience behaviour, and technical infrastructure.
                                </p>
                                <div className="sv-benefits-cards">
                                    {martechChecklist.map((item, index) => (
                                        <div key={index} className="sv-benefit-card">
                                            <i className="fas fa-check-circle"></i>
                                            <p>{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Platform Services with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    With the right set of tools in place, your digital ecosystem works smarter — not harder. Platforms are the foundation that enables strategy to turn into impactful action.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Platform Services?</h2>
                                <p className="sv-section-description">
                                    Platform services deliver maximum value for organisations seeking to optimise their marketing technology infrastructure, improve operational efficiency, and leverage sophisticated tools for competitive advantage:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Technology should amplify your strategy, not complicate it — and that's exactly what we deliver.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Partner with Reachtix for Platform Expertise?</h2>
                                <p className="sv-section-description">
                                    Choosing a platform is not just about access — it's about implementation, optimisation and strategic activation. At Reachtix, we help you:
                                </p>
                                <div className="sv-questions-grid">
                                    {partnerBenefits.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-check-circle"></i>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-3">
                                    Technology should amplify your strategy, not complicate it — and that's exactly what we deliver.
                                </p>
                                <div className="sv-questions-grid mt-4">
                                    <h4>Our Platform Expertise:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-clipboard-check"></i>
                                        Strategic Platform Selection & Technology Architecture Design
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-network-wired"></i>
                                        Cross-Platform Integration & Unified Technology Stack Development
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-sliders-h"></i>
                                        Platform Configuration, Optimisation, & Performance Management
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-graduation-cap"></i>
                                        Team Training & Best Practices Implementation for Sustainable Success
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Build a Powerful Digital Stack?</h2>
                                <p className="sv-cta-description">
                                    Your digital success depends on more than strategy — it depends on the right tools and execution.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat platform expertise as strategic capability — not just technical implementation. Our specialists blend technical proficiency with business insight to make your marketing technology a powerful driver of operational efficiency, campaign performance, and sustainable business growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-tools mr-2"></i>
                                        Discuss Platform Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Contact Reachtix Today
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TechnologyPage;